import React, { useEffect, useState } from "react";
import { useOccasionTheme } from "@/hooks/useOccasionTheme";

interface FlyingItem {
  id: number;
  type: "heart" | "logo";
  left: number;
  delay: number;
  duration: number;
  size: number;
}

const ValentineDecorations: React.FC = () => {
  const { activeTheme } = useOccasionTheme();
  const [flyingItems, setFlyingItems] = useState<FlyingItem[]>([]);
  const [hasLanded, setHasLanded] = useState(false);

  // Only render if Valentine's theme is active
  const isValentine = activeTheme?.slug === "valentines";

  useEffect(() => {
    if (!isValentine) return;

    // Initial burst on landing - 5 hearts + 1 logo
    const initialItems: FlyingItem[] = [
      ...Array.from({ length: 5 }, (_, i) => ({
        id: Date.now() + i,
        type: "heart" as const,
        left: 10 + i * 18,
        delay: i * 0.3,
        duration: 6 + Math.random() * 2,
        size: 18 + Math.random() * 10,
      })),
      {
        id: Date.now() + 100,
        type: "logo" as const,
        left: 45,
        delay: 0.5,
        duration: 8,
        size: 24,
      },
    ];
    setFlyingItems(initialItems);
    setHasLanded(true);

    // Clear initial items after they float away
    const clearInitial = setTimeout(() => {
      setFlyingItems([]);
    }, 10000);

    // After landing, send one heart every 30 seconds
    const interval = setInterval(() => {
      const newItem: FlyingItem = {
        id: Date.now(),
        type: "heart",
        left: 10 + Math.random() * 80,
        delay: 0,
        duration: 8,
        size: 20 + Math.random() * 8,
      };
      setFlyingItems((prev) => [...prev, newItem]);

      // Remove after animation completes
      setTimeout(() => {
        setFlyingItems((prev) => prev.filter((item) => item.id !== newItem.id));
      }, 9000);
    }, 30000);

    return () => {
      clearTimeout(clearInitial);
      clearInterval(interval);
    };
  }, [isValentine]);

  if (!isValentine) {
    return null;
  }

  return (
    <>
      {/* Flying Hearts & Logo */}
      <div className="fixed inset-0 pointer-events-none z-40" style={{ overflow: 'visible' }}>
        {flyingItems.map((item) => (
          <div
            key={item.id}
            className="absolute top-0 animate-float-heart"
            style={{
              left: `${item.left}%`,
              animationDelay: `${item.delay}s`,
              animationDuration: `${item.duration}s`,
              fontSize: `${item.size}px`,
            }}
          >
            {item.type === "heart" ? (
              <span className="text-pink-500/70">❤️</span>
            ) : (
              <span className="bg-gradient-to-r from-pink-500 to-rose-500 text-white px-3 py-1 rounded-full text-sm font-medium shadow-lg whitespace-nowrap">
                💕 Happy Valentine's
              </span>
            )}
          </div>
        ))}
      </div>

      {/* Subtle pink overlay gradient at edges */}
      <div className="fixed inset-0 pointer-events-none z-30">
        <div className="absolute top-0 left-0 right-0 h-16 bg-gradient-to-b from-pink-500/5 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-rose-500/5 to-transparent" />
      </div>
    </>
  );
};

export default ValentineDecorations;
